import { type Theme } from "../types";
import { DEFAULT_THEME_COLORS } from "./generateThemeCSS";
import { getPlatformName } from "../programConstants";

/**
 * Post-login branding resolution.
 *
 * An enterprise and its child firms can share a custom_domain: pre-login the
 * domain resolves to the enterprise's branding, post-login the experience
 * follows the logged-in user's firm. Every lookup here walks the same chain:
 *
 *   firm → parent (enterprise) firm → domain config → app defaults
 *
 * `firmsData` is the raw `/enterprise/firms/` payload (snake_case fields,
 * `meta_data` as a plain object) — available server-side from
 * retrieveFirmData() and client-side via getFirmsData().
 */

export const getValidColor = (color: any, fallback: string): string => {
  return color &&
    typeof color === "string" &&
    color.trim() &&
    color !== "null" &&
    color !== "undefined"
    ? color.trim()
    : fallback;
};

const findFirm = (
  firmId: string | number | null | undefined,
  firmsData: any[],
): any => {
  if (!firmId || !firmsData?.length) {
    return undefined;
  }
  return firmsData.find((x: any) => x?.id?.toString() === firmId.toString());
};

export interface ResolvedFirmBranding {
  brandPrimary: string;
  brandSecondary: string;
  logo?: string;
}

export const resolveFirmBranding = (
  firmId: string | number | null | undefined,
  firmsData: any[],
  domainFallback: { themeColors?: Theme; logo?: string } = {},
): ResolvedFirmBranding => {
  const firm = findFirm(firmId, firmsData);
  const parent = findFirm(firm?.parent_firm_id, firmsData);
  const domainColors = domainFallback.themeColors ?? DEFAULT_THEME_COLORS;

  return {
    brandPrimary: getValidColor(
      firm?.primary_color,
      getValidColor(
        parent?.primary_color,
        getValidColor(
          domainColors.brandPrimary,
          DEFAULT_THEME_COLORS.brandPrimary,
        ),
      ),
    ),
    brandSecondary: getValidColor(
      firm?.secondary_color,
      getValidColor(
        parent?.secondary_color,
        getValidColor(
          domainColors.brandSecondary,
          DEFAULT_THEME_COLORS.brandSecondary,
        ),
      ),
    ),
    logo: firm?.logo || parent?.logo || domainFallback.logo || undefined,
  };
};

/**
 * The platform name the user sees, from the firm's own branding — the same
 * `meta_data.title` that names the browser tab — walking
 * firm → parent (enterprise) firm → domain title → program name.
 * Never firm_name: that is the internal CRM name.
 */
export const resolvePlatformName = (
  firmId: string | number | null | undefined,
  firmsData: any[],
  domainTitle?: string | null,
): string => {
  const title = resolveFirmMetaValue(
    firmId,
    "title",
    firmsData,
    domainTitle || undefined,
  );
  return typeof title === "string" && title.trim()
    ? title.trim()
    : getPlatformName();
};

const hasMetaValue = (value: any): boolean =>
  value !== undefined && value !== null && value !== "";

export const resolveFirmMetaValue = (
  firmId: string | number | null | undefined,
  key: string,
  firmsData: any[],
  domainFallback?: any,
): any => {
  const firm = findFirm(firmId, firmsData);
  if (hasMetaValue(firm?.meta_data?.[key])) {
    return firm.meta_data[key];
  }
  const parent = findFirm(firm?.parent_firm_id, firmsData);
  if (hasMetaValue(parent?.meta_data?.[key])) {
    return parent.meta_data[key];
  }
  return domainFallback;
};
